import React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { percentageToolTipFormatter, percentageFormatter, generateCompletionRateSeries } from 'helpers';
import { reduceLayerTwoData, reduceCreativesData, reduceNameBasedData, shouldShowTotalActivities } from 'helpers/data';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { generateCalloutComponents } from 'helpers/componentGenerators';

import { fetchActivities } from 'services/activities/duck';
import { fetchChannel, fetchChannelCallouts, fetchChannelCreatives, fetchChannelDynamicCreatives, fetchChannelTargetingTactics } from 'services/channel/duck';
import { fetchLayerTwoChannelSummary } from 'services/layerTwo/duck';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchTacticPlacements } from 'services/tactics/duck';
import { setObjectiveShadow } from 'services/config/duck';
import { fetchSitesSummaryData } from 'services/ttd/duck';


import { LayerTwoDashboardComponent } from 'containers/dashboards/DashboardBaseComponent';
import ObjectiveSwitcher from 'components/molecules/ObjectiveSwitcher';
import { Grid, Row } from 'react-bootstrap';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import ExportPDF from 'containers/buttons/ExportPDF';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';


// Charts
import ActivitiesStackedAreaChart from 'components/molecules/charts/ActivitiesStackedAreaChart';
import GroupedBarChart from 'components/molecules/charts/GroupedBarChart';
import LineChart from 'components/molecules/charts/LineChart';

// Tables
import ActivitiesTable from 'components/molecules/tables/ActivitiesTable';
import PlacementsTable from 'components/molecules/tables/PlacementsTable';
import ProtectedCreativesReports from 'components/organisms/ProtectedCreativesReports';
import ProtectedDynamicCreativesReports from 'components/organisms/ProtectedDynamicCreativesReports';
import ProtectedTargetingTacticsTable from 'components/organisms/ProtectedTargetingTacticsTable';

// LayerTwo Charts & Tables
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';
import SitesSummaryTable from 'components/molecules/tables/SitesSummaryTable';

const filterEmptyActivitiesColumn = (col, activitiesRenderFlag) =>
    col !== 'ecpa_activities' || (activitiesRenderFlag && col === 'ecpa_activities');

class LayerTwoTVContainer extends LayerTwoDashboardComponent {
    constructor(props) {
        super(props, 'tv');
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();
        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entityMeta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchLayerTwoChannelSummary(payload);
        this.props.fetchChannel(payload);
        this.props.fetchChannelCallouts(payload);
        this.props.fetchChannelCreatives(payload);
        this.props.fetchChannelDynamicCreatives(payload);
        this.props.fetchActivities(payload);
        this.props.fetchTacticPlacements(payload);
        this.props.fetchChannelTargetingTactics(payload);
        this.props.fetchSitesSummaryData(payload);
    }

    render() {
        const channel = this.getCurrentChannel();
        const stub = `l2-${channel}`;
        const {
            backfillBuckets,
            xAxisLabel,
            entityObjective,
            entityObjectiveLabelCode,
            showTotalActivities,
            showTargetingTactics,
            showConversionRevenue,
            selectedTimeRange,
            showTtdSitesSummary
        } = this.props;

        const {
            isLabelCoded,
            eCPAActivitiesOrClicks,
            eCPAActivitiesOrClicksTerm,
            showActivitiesData,
            calloutTitle,
            helpKeySuffix,
            primaryActivityFieldTerm,
            singularTerm,
            primaryActivityField
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const storeData = this.props.layerTwo[channel];
        const hasLayerTwoSummaryData = get(storeData, 'summary.hasData', false);

        let childSummaryTableData = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(storeData.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
        }

        const hasCalloutData = get(this.props, 'layerThreeChannel.callouts.params.hasData', false);
        const hasLayerThreePlacementsData = get(this.props, 'layerThreePlacements.params.hasData', null);
        const hasLayerThreeTargetingTacticsData = get(this.props, 'layerThreeChannel.targetingTactics.params.hasData', null);
        const hasLayerThreeChannelData = get(this.props, 'layerThreeChannel.hasData', false);
        const hasCreativesData = get(this.props, 'layerThreeChannel.creatives.params.hasData', null);
        const hasDynamicCreativesData = get(this.props, 'layerThreeChannel.dynamicCreatives.params.hasData', null);
        const hasTtdSitesSummaryData = get(this.props, 'ttd.sitesSummary.hasData', null);

        const callouts = get(this.props, 'layerThreeChannel.callouts.data', {});
        const calloutWindow = get(this.props, 'layerThreeChannel.callouts.params.timeRanges.previous.windowSize', null);
        const layerThreeChannelData = get(this.props, 'layerThreeChannel.data', {});
        const placements = get(this.props, 'layerThreePlacements', {});
        const targetingTactics = get(this.props, 'layerThreeChannel.targetingTactics', {});
        const creatives = get(this.props, 'layerThreeChannel.creatives', {});
        const sitesSummaryData = get(this.props, 'ttd.sitesSummary.data', []);

        const activities = get(this.props, 'layerThreeActivities', {});
        const hasActivities = get(this.props, 'layerThreeActivities.hasData', null);
        const activitiesRenderFlag = hasActivities && (showActivitiesData || showTotalActivities);
        const userId = get(this.props, 'currentUser.id', null);
        const entityId = this.getCurrentEntityId();

        let placementsData = null;
        const placementsParams = get(this.props, 'layerThreePlacements.params', {});
        if (hasLayerThreePlacementsData) {
            placementsData = reduceNameBasedData({
                data: placements,
                namesArrayKey: 'placements',
                injectedKey: 'placementName'
            });
        }

        let targetingTacticsData = {};
        const targetingTacticsParams = get(this.props, 'layerThreeChannelData.targetingTactics.params', {});
        if (hasLayerThreeTargetingTacticsData) {
            targetingTacticsData = reduceNameBasedData({
                data: targetingTactics,
                namesArrayKey: 'targetingTactics',
                injectedKey: 'targetingTacticName'
            });
        }

        let creativesData = null;
        const creativesParams = get(this.props, 'layerThreeChannel.creatives.params', {});
        if (hasCreativesData) {
            creativesData = reduceCreativesData(creatives, { filterField: 'started_plays' });
        }

        const isChannelAvailable = get(this.props, 'entityMeta.channelAvailability.tv', false);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');

        const calloutSettings = [{
            key: 'impressions',
            title: 'Impressions',
            format: '0,0'
        },
        { key: 'completed_plays', title: 'Completed Views', format: '0,0' }];
        if (hasActivities) {
            calloutSettings.push({ key: eCPAActivitiesOrClicks, title: calloutTitle, helpKey: `ecpa${helpKeySuffix}`, format: '0,0' });
        }
        calloutSettings.push(showActivitiesData ?
            { key: 'completion_rate', title: 'Completion Rate', format: '0,0.00%' } :
            { key: 'ctr', title: 'CTR', format: '0.00%' }
        );

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded
        });

        const showLayerThreePlacements = hasLayerThreePlacementsData && activitiesRenderFlag;
        const { orderedColors } = this.props.theme;

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} />}
        </React.Fragment>;

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Advanced TV
                </ChannelTitle>
                {this.props.enableObjectiveSwitching && <ObjectiveSwitcher
                    setObjectiveShadow={this.props.setObjectiveShadow}
                    fetchEntityMeta={this.props.fetchEntityMeta}
                    entityId={this.getCurrentEntityId()}
                    objective={this.props.entityObjective}
                    objectiveLabelCode={this.props.entityObjectiveLabelCode}
                />}
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs */}
                <Row>
                    {calloutComponents}
                </Row>
                {/* Child Tables */}

                {hasLayerTwoSummaryData ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} OVERVIEW`}
                        channel={this.getCurrentChannel()}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        exportName={`${stub}-table-performance`}
                        showTotalActivities={showTotalActivities}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['impressions', 'clicks', 'completion_rate', 'spend'] :
                                ['impressions', 'ecpa_activities', 'completion_rate', 'spend']
                        }
                        cacheKey={`${stub}-table-performance-${userId}-${this.getCurrentEntityId()}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={hasLayerTwoSummaryData} />
                )}

                {/* Advanced TV Quartile Performance */}

                {hasLayerThreeChannelData ? (
                    <LineChart
                        title="COMPLETION RATE"
                        exportName={`${stub}-completion-rate`}
                        series={generateCompletionRateSeries(layerThreeChannelData, orderedColors)}
                        data={{ [this.state.channel]: layerThreeChannelData }}
                        backfillBuckets={backfillBuckets}
                        yTitle="Completion Rate"
                        yOptions={{
                            tooltip: {
                                pointFormatter: percentageToolTipFormatter
                            }
                        }}
                        yAxisOptions={{
                            max: 1, // this prevents the chart from showing over 100%
                            labels: {
                                formatter: percentageFormatter
                            }
                        }}
                        y="completion_rate"
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasLayerThreeChannelData} />
                )}

                { showTtdSitesSummary && (
                    hasTtdSitesSummaryData
                        ? <SitesSummaryTable data={sitesSummaryData} cacheKey={`lq-sites-summary-table-${userId}-${entityId}`}/>
                        : <RenderingTile height='400px' />
                )}

                {/* Placements */}

                {(showLayerThreePlacements) ? (
                    <GroupedBarChart
                        title="PLACEMENT PERFORMANCE"
                        exportName={`${stub}-placement-performance`}
                        data={placementsData.graph}
                        keys={placementsData.sortedKeys}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        y={eCPAActivitiesOrClicks}
                        yAxisLabel={eCPAActivitiesOrClicksTerm}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={showLayerThreePlacements} />
                )}

                {(showLayerThreePlacements) ? (
                    <PlacementsTable
                        title={`PLACEMENT ${singularTerm.toUpperCase()} BREAKDOWN`}
                        exportName={`${stub}-placement-activity-breakdown`}
                        data={placementsData.table}
                        params={placementsParams}
                        hasData={hasLayerThreePlacementsData}
                        showTotalActivities={showTotalActivities}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['placement', 'impressions', 'clicks', 'ctr', 'completion_rate', 'spend']
                                : ['placement', 'impressions', 'ecpa_activities', 'completion_rate', 'spend']
                                    .filter(col => filterEmptyActivitiesColumn(col, activitiesRenderFlag))
                        }
                        defaultSorted={[
                            {
                                id: 'cpcv',
                                desc: true
                            }
                        ]} />
                ) : (
                    <RenderingTile height='400px' show={showLayerThreePlacements} />
                )}

                <ProtectedTargetingTacticsTable
                    title="TARGETING TACTICS BREAKDOWN"
                    exportName={`${stub}-targeting-tactics-breakdown`}
                    tableType="targetingTactics"
                    data={targetingTacticsData.table}
                    params={targetingTacticsParams}
                    hasData={hasLayerThreeTargetingTacticsData}
                    showTotalActivities={showTotalActivities}
                    showTargetingTactics={showTargetingTactics}
                    showConversionRevenue={showConversionRevenue}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    accessors={
                        entityObjective === 'ctr' ?
                            ['targetingTactic', 'impressions', 'clicks', 'ctr', 'completion_rate']
                            : ['targetingTactic', 'impressions', 'ecpa_activities', 'completion_rate']
                                .filter(col => filterEmptyActivitiesColumn(col, activitiesRenderFlag))
                    }
                    defaultSorted={[
                        {
                            id: 'cpcv',
                            desc: true
                        }
                    ]} />

                {/* Activities */}
                {activitiesRenderFlag ? (
                    <ActivitiesStackedAreaChart
                        title={`${singularTerm.toUpperCase()} TRENDS`}
                        exportName={`${stub}-activity-trends`}
                        exportLimit={10}
                        data={activities.data}
                        keys={activities.rankings[primaryActivityField].map(d => d.name).reverse()}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel={primaryActivityFieldTerm}
                        y={primaryActivityField}
                        colors={this.props.theme.orderedColors}
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                {activitiesRenderFlag ? (
                    <ActivitiesTable
                        title={`${singularTerm.toUpperCase()} BREAKDOWN`}
                        exportName={`${stub}-website-activity-breakdown`}
                        data={activities}
                        showTotalActivities={showTotalActivities}
                        objectives={[entityObjective]}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        filterField={showTotalActivities ? 'total_activities' : primaryActivityField}
                    />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                <ProtectedCreativesReports
                    exportName={`${stub}-creatives`}
                    hasCreativesData={hasCreativesData}
                    creativesData={creativesData}
                    creativesParams={creativesParams}
                    eCPAActivitiesOrClicks={eCPAActivitiesOrClicks}
                    eCPAActivitiesOrClicksTerm={eCPAActivitiesOrClicksTerm}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    showTotalActivities={showTotalActivities}
                    showConversionRevenue={showConversionRevenue}
                    activitiesOrClicksRenderFlag={activitiesRenderFlag}
                    accessors={['label', 'startedPlays', 'completed_plays', 'completion_rate']}
                />

                <ProtectedDynamicCreativesReports
                    exportName={`${stub}-dynamic-creatives`}
                    hasDynamicCreativesData={hasDynamicCreativesData}
                    dynamicCreatives={get(this.props, 'layerThreeChannel.dynamicCreatives', {})}
                    dynamicProfiles={get(this.props, 'entityMeta.dynamicProfiles', {})}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    entityObjective={entityObjective}
                />
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    Advanced TV
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                <NoChannelDataTile channel='Advanced TV' />
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entityMeta: state.entities.meta,
    objectiveShadow: state.config.objectiveShadow,
    enableObjectiveSwitching: get(state, 'entities.meta.meta.enableObjectiveSwitching', false),
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showTotalActivities: shouldShowTotalActivities(state),
    showTargetingTactics: get(state, 'entities.meta.meta.showTargetingTactics', false),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    showTtdSitesSummary: get(state, 'entities.meta.meta.showTtdSitesSummary', false),
    layerTwo: state.layerTwo,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),
    ttd: state.ttd,

    // LayerThree Services
    layerThreeChannel: state.channel.tv,
    layerThreePlacements: state.tactics.placements,
    layerThreeActivities: state.activities.tv
});

export default connect(mapStateToProps, {
    fetchActivities,
    fetchChannel,
    fetchChannelCallouts,
    fetchChannelCreatives,
    fetchChannelDynamicCreatives,
    fetchChannelTargetingTactics,
    fetchSitesSummaryData,
    fetchEntityMeta,
    fetchLayerTwoChannelSummary,
    fetchTacticPlacements,
    setObjectiveShadow,
})(LayerTwoTVContainer);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerTwoTVContainer.js